// src/components/login/signup-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// The login page's sibling, and it moves the same way: `data-kit` on the root,
// `AuthCard` for the box, kit fields inside it. `ui/input`, `ui/button` and
// `ui/alert` are gone.
//
// What did NOT move: `useAuth`, `signup`, `checkPassword` and the policy it
// mirrors, and every string. This is presentation only.
//
// Three things the kit made better rather than merely different:
//
//   * The password reveal was a bare <button> with an inline style. It is
//     `PasswordInput` now, which owns and labels its own reveal.
//   * The submit greyed itself out through `background: var(--muted)` while
//     staying enabled to a screen reader. `Button` takes `disabled`, so the
//     look and the state are one fact.
//   * The two legal links were <button>s painted to look like links. They are
//     `InlineLink`, which is what they are.
//
// The card is 400 wide now rather than 720, because that is item 159's
// redline and the specimen wins.
import { useState, FormEvent } from "react";
import { useAuth } from "../../hooks/login/useAuth";
import { UpmodeLogo } from "./upmode-logo";
import { checkPassword, PASSWORD_MIN_LENGTH } from "./password-requirements";
import {
  AuthCard, Banner, Button, FieldRow, FormStack, InlineLink,
  PasswordInput, TextInput, Validation,
} from "../kit";

interface SignupPageProps {
  onSignupSuccess?: () => void;
  onNavigateToLogin?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

export function SignupPage({
  onSignupSuccess,
  onNavigateToLogin,
  onNavigateToTerms,
  onNavigateToPrivacy,
}: SignupPageProps) {
  const { signup, isLoading, error, clearError } = useAuth();

  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");

  const pwChecks = checkPassword(password);
  const canSubmit = !isLoading && !!name && !!email && pwChecks.allValid;

  // The checklist is DERIVED from the same `checkPassword` the submit guard
  // uses, so a rule cannot be listed as met while the guard disagrees. That is
  // the "derive, never retype" rule applied to a boolean rather than a figure.
  const requirements: { met: boolean; text: string }[] = [
    { met: pwChecks.length, text: `At least ${PASSWORD_MIN_LENGTH} characters` },
    { met: pwChecks.upper,  text: "One uppercase letter (A to Z)" },
    { met: pwChecks.lower,  text: "One lowercase letter (a to z)" },
    { met: pwChecks.digit,  text: "One number (0 to 9)" },
    { met: pwChecks.symbol, text: "One symbol (! @ # $ % ^ & * etc.)" },
  ];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await signup({ name, email, password });
      onSignupSuccess?.();
    } catch {
      // surfaced via context.error
    }
  };

  return (
    <div className="upm-auth-page" data-kit>
      <div className="upm-auth-page__wash" aria-hidden="true" />

      <div className="upm-auth-page__column">
        <div className="upm-auth-page__brand">
          <UpmodeLogo />
        </div>

        <AuthCard title="Create your account">
          {error ? <Banner tone="error">{error.message}</Banner> : null}

          <form onSubmit={handleSubmit}>
            <FormStack>
              <FieldRow label="Full name" required>
                {({ id }) => (
                  <TextInput
                    id={id}
                    placeholder="Jane Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    state={isLoading ? "disabled" : undefined}
                  />
                )}
              </FieldRow>

              <FieldRow label="Email address" required>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="email"
                    placeholder="jane@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    state={isLoading ? "disabled" : undefined}
                  />
                )}
              </FieldRow>

              <FieldRow label="Password" required>
                {({ id }) => (
                  <PasswordInput
                    id={id}
                    placeholder="Choose a strong password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    state={
                      isLoading ? "disabled"
                      : password && !pwChecks.allValid ? "error"
                      : undefined
                    }
                  />
                )}
              </FieldRow>

              {password ? (
                <div className="upm-auth-page__reqs">
                  <span className="k-meta">Password requirements</span>
                  {requirements.map((r) => (
                    <Validation key={r.text} tone={r.met ? "ok" : "info"}>
                      {r.text}
                    </Validation>
                  ))}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={!canSubmit}
                loading={isLoading}
                loadingLabel="Creating account"
                className="upm-auth-page__submit"
              >
                Create account
              </Button>
            </FormStack>
          </form>

          <p className="upm-auth-page__legal">
            By creating an account, you agree to our{" "}
            <InlineLink
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigateToTerms?.(); }}
            >
              Terms of Service
            </InlineLink>{" "}
            and{" "}
            <InlineLink
              href="#"
              onClick={(e) => { e.preventDefault(); onNavigateToPrivacy?.(); }}
            >
              Privacy Policy
            </InlineLink>.
          </p>
        </AuthCard>

        <p className="upm-auth-page__legal">
          Already have an account?{" "}
          <InlineLink
            href="#"
            onClick={(e) => { e.preventDefault(); if (!isLoading) onNavigateToLogin?.(); }}
          >
            Sign in
          </InlineLink>
        </p>
      </div>
    </div>
  );
}
